import { type PortableTextBlock, stegaClean } from 'next-sanity'

import PortableText from '@/app/components/PortableText'
import { TRACKING_LOCATIONS } from '@/app/utils/analytics'
import { cn } from '@/app/utils/style'
import type { Group as GroupType } from '@/sanity.types'
import { dataAttr } from '@/sanity/lib/utils'

type GroupProps = {
  value: GroupType & { _key?: string }
  documentId?: string
  documentType?: string
  parentPath?: string
  /** Forwarded to nested contentImage blocks; 'contain' shows images uncropped */
  imageFit?: 'cover' | 'contain'
  /** Lets links and buttons wrap instead of staying on one line (long URLs in articles) */
  wrapLinks?: boolean
}

export function Group({
  value,
  documentId,
  documentType,
  parentPath,
  imageFit,
  wrapLinks = false,
}: GroupProps) {
  const { _key, content, gap, align, justify, columns, wrapEachElement } = value

  if (!content?.length) {
    return null
  }

  // Column settings
  const columnsDefault = stegaClean(columns?.default) || 1
  const columnsMd = stegaClean(columns?.md)
  const columnsLg = stegaClean(columns?.lg)

  // Fit content settings
  const fitContentDefault = stegaClean(columns?.fitContentDefault)
  const fitContentMd = stegaClean(columns?.fitContentMd)
  const fitContentLg = stegaClean(columns?.fitContentLg)

  // Always use grid layout (no flex)
  const layoutClasses = 'grid'

  // Build grid-template-columns based on fitContent settings
  // When fitContent is true, use 'auto' sizing; otherwise use standard grid-cols-N (1fr)
  const getGridCols = (columns: number, fitContent: boolean) => {
    if (columns === 1) return 'grid-cols-1'
    if (fitContent) return `grid-cols-auto-${columns}`
    return `grid-cols-${columns}`
  }

  const gridColsClasses = cn(
    getGridCols(columnsDefault, !!fitContentDefault),
    columnsMd && `md:${getGridCols(columnsMd, !!fitContentMd)}`,
    columnsLg && `lg:${getGridCols(columnsLg, !!fitContentLg)}`,
  )

  // Gap classes
  const gapDefault = stegaClean(gap?.default) || 'md'
  const gapMd = stegaClean(gap?.md)
  const gapLg = stegaClean(gap?.lg)

  const gapClasses = cn(`gap-${gapDefault}`, gapMd && `md:gap-${gapMd}`, gapLg && `lg:gap-${gapLg}`)

  // Align classes (grid only)
  const getAlignClass = (value: string) => {
    return {
      start: 'items-start',
      center: 'items-center',
      end: 'items-end',
      stretch: 'items-stretch',
    }[value]
  }

  // Justify classes (grid only)
  const getJustifyClass = (value: string) => {
    return {
      start: 'justify-items-start',
      center: 'justify-items-center',
      end: 'justify-items-end',
      stretch: 'justify-items-stretch',
    }[value]
  }

  // Extract responsive align/justify values
  const alignDefault = stegaClean(align?.default)
  const alignMd = stegaClean(align?.md)
  const alignLg = stegaClean(align?.lg)

  const justifyDefault = stegaClean(justify?.default)
  const justifyMd = stegaClean(justify?.md)
  const justifyLg = stegaClean(justify?.lg)

  // Responsive align classes
  const alignClasses = cn(
    alignDefault && getAlignClass(alignDefault),
    alignMd && `md:${getAlignClass(alignMd)}`,
    alignLg && `lg:${getAlignClass(alignLg)}`,
  )

  // Responsive justify classes
  const justifyClasses = cn(
    justifyDefault && getJustifyClass(justifyDefault),
    justifyMd && `md:${getJustifyClass(justifyMd)}`,
    justifyLg && `lg:${getJustifyClass(justifyLg)}`,
  )

  // Create data attribute for presentation mode if we have all required context
  const attr =
    _key && documentId && documentType && parentPath
      ? dataAttr({
          id: documentId,
          type: documentType,
          path: `${parentPath}[_key=="${_key}"]`,
        })
      : undefined

  const shouldWrap = stegaClean(wrapEachElement)

  // Get text alignment classes for wrapper divs (grid mode with justify-items)
  const getWrapperTextAlign = (justifyValue: string) => {
    return {
      start: 'text-left',
      center: 'text-center',
      end: 'text-right',
      stretch: '',
    }[justifyValue]
  }

  // Wrapper classes for wrapEachElement mode
  const wrapperClasses = cn(
    '[&_*:first-child]:mt-0 [&_*:last-child]:mb-0',
    justifyDefault && getWrapperTextAlign(justifyDefault),
    justifyMd && getWrapperTextAlign(justifyMd) && `md:${getWrapperTextAlign(justifyMd)}`,
    justifyLg && getWrapperTextAlign(justifyLg) && `lg:${getWrapperTextAlign(justifyLg)}`,
  )

  // Text alignment classes for nested content blocks
  const textAlignClasses = cn(
    justifyDefault && getWrapperTextAlign(justifyDefault),
    justifyMd && getWrapperTextAlign(justifyMd) && `md:${getWrapperTextAlign(justifyMd)}`,
    justifyLg && getWrapperTextAlign(justifyLg) && `lg:${getWrapperTextAlign(justifyLg)}`,
  )

  return (
    <div
      className={cn(
        layoutClasses,
        gridColsClasses,
        gapClasses,
        alignClasses,
        justifyClasses,
        !wrapLinks && '[&_a]:whitespace-nowrap [&_button]:whitespace-nowrap',
        wrapLinks && '[&>*]:min-w-0 [&_a]:break-words',
        '[&_:has(>.size-header-h6)+*_[class~="lg:size-header-h3"]]:font-normal',
      )}
      {...(attr && { 'data-sanity': attr.toString() })}
    >
      {shouldWrap ? (
        // Wrap each element separately
        content.map((block, index) => (
          <div key={block._key || index} className={wrapperClasses}>
            <PortableText
              value={[block] as PortableTextBlock[]}
              tracking={{ location: TRACKING_LOCATIONS.PAGE_CONTENT }}
              asFragment
              imageFit={imageFit}
              documentId={documentId}
              documentType={documentType}
              parentPath={
                _key && parentPath && block._key
                  ? `${parentPath}[_key=="${_key}"].content[_key=="${block._key}"]`
                  : parentPath
              }
              className={textAlignClasses}
            />
          </div>
        ))
      ) : (
        // Render all content together (default behavior)
        <PortableText
          value={content as PortableTextBlock[]}
          tracking={{ location: TRACKING_LOCATIONS.PAGE_CONTENT }}
          asFragment
          imageFit={imageFit}
          documentId={documentId}
          documentType={documentType}
          parentPath={_key && parentPath ? `${parentPath}[_key=="${_key}"].content` : parentPath}
          className={textAlignClasses}
        />
      )}
    </div>
  )
}
